import { useState, useEffect } from 'react';
import { produtosService, categoriasService, mesasService, pedidosService, caixaService, comandasService, clientesService, displayService, configService } from '../../services';
import { formatCurrency } from '../../utils/format';
import { imprimirComanda, imprimirCobranca } from '../../utils/print';
import { useAuth } from '../../hooks/useAuth';
import './PDV.css';

const STATUS_PROD = {
  fila:       { label: 'Fila',       color: '#f57c00' },
  preparando: { label: 'Preparando', color: '#1976d2' },
  pronto:     { label: 'Pronto',     color: '#388e3c' },
  entregue:   { label: 'Entregue',   color: '#757575' },
};

export default function PDV() {
  const { user } = useAuth();
  const podeCancelarItem = ['proprietario', 'administrador', 'funcionario', 'socio'].includes(user?.perfil);
  const [produtos, setProdutos] = useState([]);
  const [categorias, setCategorias] = useState([]);
  const [mesas, setMesas] = useState([]);
  const [categoriaAtiva, setCategoriaAtiva] = useState('todas');
  const [busca, setBusca] = useState('');
  const [mobileView, setMobileView] = useState('produtos'); // 'produtos' | 'carrinho' (somente <=768px)
  const [carrinho, setCarrinho] = useState([]);
  const [tipo, setTipo] = useState('balcao');
  const [mesaId, setMesaId] = useState('');
  const [consumoMesa, setConsumoMesa] = useState({ comandas: [], sem_comanda: { itens: [], total: 0 }, pedidos_ids: [], total: 0, loading: false });
  const [modalPagamento, setModalPagamento] = useState(false);
  const [forma, setForma] = useState('dinheiro');
  const [bandeira, setBandeira] = useState('');
  const [valorRecebido, setValorRecebido] = useState('');
  const [operadoras, setOperadoras] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processando, setProcessando] = useState(false);
  const [ultimaVenda, setUltimaVenda] = useState(null);
  const [comandaParaPagar, setComandaParaPagar] = useState(null); // { id, numero, cliente_nome, total }
  const [formaComanda, setFormaComanda] = useState('dinheiro');
  const [bandeiraComanda, setBandeiraComanda] = useState('');
  const [valorRecebidoComanda, setValorRecebidoComanda] = useState('');
  const [processandoComanda, setProcessandoComanda] = useState(false);
  // Modal de cancelamento de item
  const [itemParaCancelar, setItemParaCancelar] = useState(null); // { comanda, item }
  const [cancelandoItem, setCancelandoItem] = useState(false);

  // Busca rápida por número de comanda
  const [buscaComandaNum, setBuscaComandaNum] = useState('');
  const [resultadoComanda, setResultadoComanda] = useState(null); // { id, numero, mesa_id, mesa_numero, cliente_nome, total }
  const [buscandoComanda, setBuscandoComanda] = useState(false);

  // Seleção de cliente para pagamento fiado
  const [buscaCliente, setBuscaCliente] = useState('');
  const [resultadosCliente, setResultadosCliente] = useState([]);
  const [clienteSelecionado, setClienteSelecionado] = useState(null); // { id, nome, telefone }

  useEffect(() => {
    Promise.all([
      produtosService.listar({ status: 'ativo' }),
      categoriasService.listar(),
      mesasService.listar(),
    ]).then(([pRes, cRes, mRes]) => {
      setProdutos(pRes.data);
      setCategorias(cRes.data);
      setMesas(mRes.data);
    }).finally(() => setLoading(false));
  }, []);

  // Operadoras de cartão cadastradas (Configurações > Pagamentos)
  useEffect(() => {
    configService.taxasCartao()
      .then(({ data }) => setOperadoras(Array.isArray(data) ? data : []))
      .catch(() => {});
  }, []);

  // Carrega consumo da mesa ao selecionar
  useEffect(() => {
    if (tipo !== 'mesa' || !mesaId) {
      setConsumoMesa({ comandas: [], sem_comanda: { itens: [], total: 0 }, pedidos_ids: [], total: 0, loading: false });
      return;
    }
    setConsumoMesa(prev => ({ ...prev, loading: true }));
    mesasService.consumo(mesaId)
      .then(({ data }) => setConsumoMesa({
        comandas: data.comandas || [],
        sem_comanda: data.sem_comanda || { itens: [], total: 0 },
        pedidos_ids: data.pedidos_ids || [],
        total: data.total || 0,
        loading: false,
      }))
      .catch(() => setConsumoMesa({ comandas: [], sem_comanda: { itens: [], total: 0 }, pedidos_ids: [], total: 0, loading: false }));
  }, [tipo, mesaId]);

  const produtosFiltrados = produtos.filter(p => {
    const matchCat = categoriaAtiva === 'todas' || p.categoria_id === categoriaAtiva;
    const matchBusca = !busca || p.nome.toLowerCase().includes(busca.toLowerCase());
    return matchCat && matchBusca;
  });

  const adicionarAoCarrinho = (produto) => {
    if (produto.sem_estoque) return;
    setCarrinho(prev => {
      const ex = prev.find(i => i.produto_id === produto.id);
      if (ex) return prev.map(i => i.produto_id === produto.id ? { ...i, quantidade: i.quantidade + 1 } : i);
      return [...prev, { produto_id: produto.id, nome: produto.nome, emoji: produto.emoji, preco: parseFloat(produto.preco), quantidade: 1 }];
    });
  };

  const removerDoCarrinho = (produto_id) => {
    setCarrinho(prev => {
      const item = prev.find(i => i.produto_id === produto_id);
      if (!item) return prev;
      if (item.quantidade > 1) return prev.map(i => i.produto_id === produto_id ? { ...i, quantidade: i.quantidade - 1 } : i);
      return prev.filter(i => i.produto_id !== produto_id);
    });
  };

  const totalCarrinho = carrinho.reduce((s, i) => s + i.preco * i.quantidade, 0);
  const totalConsumoMesa = tipo === 'mesa' ? consumoMesa.total : 0;
  const total = totalCarrinho + totalConsumoMesa;
  const troco = forma === 'dinheiro' && valorRecebido ? Math.max(0, parseFloat(valorRecebido) - total) : 0;

  const buscarClientes = async (texto) => {
    setBuscaCliente(texto);
    setClienteSelecionado(null);
    if (texto.length < 2) { setResultadosCliente([]); return; }
    try {
      const { data } = await clientesService.listar(texto);
      setResultadosCliente(data);
    } catch {}
  };

  const selecionarCliente = (c) => {
    setClienteSelecionado(c);
    setBuscaCliente(c.nome);
    setResultadosCliente([]);
  };

  const resetarCliente = () => {
    setClienteSelecionado(null);
    setBuscaCliente('');
    setResultadosCliente([]);
  };

  const buscarComandaPorNumero = async (e) => {
    e.preventDefault();
    const num = parseInt(buscaComandaNum);
    if (!num || num < 1 || num > 200) return;
    setBuscandoComanda(true);
    setResultadoComanda(null);
    try {
      const { data } = await comandasService.buscarPorNumero(num);
      setResultadoComanda(data.length > 0 ? data[0] : false);
    } catch {
      setResultadoComanda(false);
    } finally {
      setBuscandoComanda(false);
    }
  };

  const irParaMesaDaComanda = (comanda) => {
    setTipo('mesa');
    setMesaId(String(comanda.mesa_id));
    setBuscaComandaNum('');
    setResultadoComanda(null);
  };

  const temConsumoMesa = tipo === 'mesa' && (
    (consumoMesa.comandas?.length > 0) ||
    (consumoMesa.sem_comanda?.itens?.length > 0)
  );
  const podeCobrar = carrinho.length > 0 || temConsumoMesa;

  const confirmarPedido = async () => {
    if (!podeCobrar) return;
    if (forma === 'fiado' && !clienteSelecionado) {
      alert('Selecione o cliente para registrar o fiado');
      return;
    }
    if (forma === 'dinheiro' && valorRecebido !== '' && parseFloat(valorRecebido) < total) {
      alert('Valor recebido deve ser maior ou igual ao valor total');
      return;
    }
    setProcessando(true);
    try {
      if (temConsumoMesa) {
        if (carrinho.length > 0) {
          await pedidosService.criar({
            tipo: 'mesa',
            mesa_id: parseInt(mesaId),
            cliente_id: clienteSelecionado?.id || null,
            itens: carrinho.map(i => ({ produto_id: i.produto_id, quantidade: i.quantidade })),
          });
        }
        const { data } = await caixaService.pagarMesa({
          mesa_id: parseInt(mesaId),
          forma,
          bandeira: forma.startsWith('cartao_') ? (bandeira || null) : null,
          valor: total,
          cliente_id: clienteSelecionado?.id || null,
          valor_recebido: forma === 'dinheiro' ? parseFloat(valorRecebido) || total : null,
        });
        displayService.pago().catch(() => {});
        const mesaInfo = mesas.find(m => String(m.id) === String(mesaId));
        setUltimaVenda({ mesa: mesaInfo?.numero, total, troco: data.troco || 0 });
      } else {
        const { data: pedido } = await pedidosService.criar({
          tipo,
          mesa_id: tipo === 'mesa' ? parseInt(mesaId) || null : null,
          cliente_id: clienteSelecionado?.id || null,
          itens: carrinho.map(i => ({ produto_id: i.produto_id, quantidade: i.quantidade })),
        });
        await caixaService.pagamento({
          pedido_id: pedido.id,
          forma,
          bandeira: forma.startsWith('cartao_') ? (bandeira || null) : null,
          valor: total,
          valor_recebido: forma === 'dinheiro' ? parseFloat(valorRecebido) || total : null,
        });
        setUltimaVenda({ numero: pedido.numero, senha: pedido.senha_balcao, total, troco });
      }

      setCarrinho([]);
      setConsumoMesa({ comandas: [], sem_comanda: { itens: [], total: 0 }, pedidos_ids: [], total: 0, loading: false });
      setModalPagamento(false);
      setValorRecebido('');
      setForma('dinheiro');
      setBandeira('');
      setTipo('balcao');
      setMesaId('');
      setMobileView('produtos');
      resetarCliente();
      mesasService.listar().then(r => setMesas(r.data));
    } catch (err) {
      alert(err.response?.data?.error || 'Erro ao registrar venda');
    } finally {
      setProcessando(false);
    }
  };

  const cancelarComandaVazia = async (comanda) => {
    if (!window.confirm(`Cancelar cartão #${comanda.numero}? A comanda está vazia e será removida.`)) return;
    try {
      await comandasService.cancelar(comanda.id);
      const { data } = await mesasService.consumo(mesaId);
      const novoConsumo = {
        comandas: data.comandas || [],
        sem_comanda: data.sem_comanda || { itens: [], total: 0 },
        pedidos_ids: data.pedidos_ids || [],
        total: data.total || 0,
        loading: false,
      };
      setConsumoMesa(novoConsumo);
      if (novoConsumo.comandas.length === 0 && novoConsumo.sem_comanda.itens.length === 0) {
        setTipo('balcao');
        setMesaId('');
        setCarrinho([]);
        mesasService.listar().then(r => setMesas(r.data));
      }
    } catch (err) {
      alert(err.response?.data?.error || 'Erro ao cancelar comanda');
    }
  };

  const cancelarItemComanda = (comanda, item) => {
    setItemParaCancelar({ comanda, item });
  };

  const confirmarCancelamentoItem = async () => {
    if (!itemParaCancelar) return;
    setCancelandoItem(true);
    try {
      await comandasService.cancelarItem(itemParaCancelar.comanda.id, itemParaCancelar.item.comanda_item_id);
      const { data } = await mesasService.consumo(mesaId);
      setConsumoMesa({
        comandas: data.comandas || [],
        sem_comanda: data.sem_comanda || { itens: [], total: 0 },
        pedidos_ids: data.pedidos_ids || [],
        total: data.total || 0,
        loading: false,
      });
      setItemParaCancelar(null);
    } catch (err) {
      alert(err.response?.data?.error || 'Erro ao cancelar item');
    } finally {
      setCancelandoItem(false);
    }
  };

  const abrirPagamentoComanda = (comanda) => {
    displayService.enviar(comanda.id).catch(() => {});
    setComandaParaPagar({ ...comanda, total: parseFloat(comanda.total) });
    setFormaComanda('dinheiro');
    setValorRecebidoComanda('');
  };

  const imprimirComandaPDV = (comanda) => {
    const mesaSel = mesas.find(m => String(m.id) === String(mesaId));
    imprimirComanda({
      titulo: 'COMANDA',
      mesa: mesaSel?.numero,
      comanda: comanda.numero,
      cliente: comanda.cliente_nome || null,
      itens: comanda.itens || [],
      total: parseFloat(comanda.total) || 0,
      mostrarPrecos: true,
    });
  };

  const imprimirCobrancaPDV = () => {
    const mesaSel = mesas.find(m => String(m.id) === String(mesaId));
    const grupos = (consumoMesa.comandas || []).map(c => ({
      pedido_numero: c.pedido_numero,
      comanda_numero: c.numero,
      cliente_nome: c.cliente_nome,
      itens: c.itens || [],
    }));
    const balcaoItens = [
      ...((consumoMesa.sem_comanda?.itens) || []),
      ...carrinho.map(i => ({ nome: i.nome, quantidade: i.quantidade, subtotal: i.preco * i.quantidade })),
    ];
    imprimirCobranca({
      mesa: tipo === 'mesa' ? mesaSel?.numero : null,
      grupos,
      balcaoItens,
      total,
    });
  };

  const confirmarPagamentoComanda = async () => {
    if (!comandaParaPagar) return;
    if (formaComanda === 'fiado' && !clienteSelecionado) {
      alert('Selecione o cliente para registrar o fiado');
      return;
    }
    if (formaComanda === 'dinheiro' && valorRecebidoComanda !== '' && parseFloat(valorRecebidoComanda) < comandaParaPagar.total) {
      alert('Valor recebido deve ser maior ou igual ao valor total');
      return;
    }
    setProcessandoComanda(true);
    try {
      const { data } = await comandasService.pagar(comandaParaPagar.id, {
        forma: formaComanda,
        bandeira: formaComanda.startsWith('cartao_') ? (bandeiraComanda || null) : null,
        cliente_id: clienteSelecionado?.id || null,
        valor_recebido: formaComanda === 'dinheiro' ? parseFloat(valorRecebidoComanda) || comandaParaPagar.total : null,
      });
      const trocoComanda = data.troco || 0;
      setComandaParaPagar(null);
      setValorRecebidoComanda('');
      setFormaComanda('dinheiro');
      setBandeiraComanda('');
      resetarCliente();
      // Recarrega consumo da mesa
      const { data: consumo } = await mesasService.consumo(mesaId);
      const novoConsumo = {
        comandas: consumo.comandas || [],
        sem_comanda: consumo.sem_comanda || { itens: [], total: 0 },
        pedidos_ids: consumo.pedidos_ids || [],
        total: consumo.total || 0,
        loading: false,
      };
      setConsumoMesa(novoConsumo);
      // Se mesa ficou sem consumo, volta ao estado inicial
      const ficouLivre = novoConsumo.comandas.length === 0 && novoConsumo.sem_comanda.itens.length === 0;
      if (ficouLivre) {
        setTipo('balcao');
        setMesaId('');
        setCarrinho([]);
        mesasService.listar().then(r => setMesas(r.data));
      }
      displayService.pago().catch(() => {});
      const nomeComanda = comandaParaPagar.cliente_nome || `Comanda #${comandaParaPagar.numero}`;
      setUltimaVenda({ comanda: nomeComanda, total: comandaParaPagar.total, troco: trocoComanda, mesaLivre: ficouLivre });
    } catch (err) {
      alert(err.response?.data?.error || 'Erro ao pagar comanda');
    } finally {
      setProcessandoComanda(false);
    }
  };

  if (loading) return <div className="loading"><div className="spinner"></div>Carregando...</div>;

  const qtdCarrinho = carrinho.reduce((s, i) => s + i.quantidade, 0);

  return (
    <div className={`pdv-container ${mobileView === 'carrinho' ? 'mostrar-carrinho' : ''}`}>
      {/* Esquerda - Produtos */}
      <div className="pdv-produtos">
        <div className="pdv-busca">
          <input
            type="text"
            placeholder="🔍 Buscar produto..."
            value={busca}
            onChange={e => setBusca(e.target.value)}
          />
        </div>
        <div className="pdv-categorias">
          <button className={`cat-btn ${categoriaAtiva === 'todas' ? 'active' : ''}`} onClick={() => setCategoriaAtiva('todas')}>
            Todos
          </button>
          {categorias.map(c => (
            <button
              key={c.id}
              className={`cat-btn ${categoriaAtiva === c.id ? 'active' : ''}`}
              onClick={() => setCategoriaAtiva(c.id)}
            >
              {c.emoji} {c.nome}
            </button>
          ))}
        </div>
        <div className="pdv-grid">
          {produtosFiltrados.map(p => (
            <button
              key={p.id}
              className={`pdv-produto-card${p.sem_estoque ? ' produto-esgotado' : ''}`}
              onClick={() => adicionarAoCarrinho(p)}
              disabled={p.sem_estoque}
              title={p.sem_estoque ? 'Sem estoque' : undefined}
            >
              <div className="pdv-produto-emoji">{p.emoji || '🥟'}</div>
              <div className="pdv-produto-nome">{p.nome}</div>
              <div className="pdv-produto-preco">{formatCurrency(p.preco)}</div>
              {p.sem_estoque && <span className="produto-esgotado-badge">Esgotado</span>}
            </button>
          ))}
          {produtosFiltrados.length === 0 && <p className="no-data">Nenhum produto encontrado</p>}
        </div>

        {(qtdCarrinho > 0 || temConsumoMesa) && (
          <button className="pdv-mobile-cart-btn" onClick={() => setMobileView('carrinho')}>
            🛒 Ver pedido{qtdCarrinho > 0 ? ` (${qtdCarrinho})` : ''} — {formatCurrency(total)}
          </button>
        )}
      </div>

      {/* Direita - Carrinho */}
      <div className="pdv-carrinho">
        <button className="pdv-mobile-back" onClick={() => setMobileView('produtos')}>← Produtos</button>
        <h2>🛒 Pedido</h2>

        {/* Busca rápida por número de comanda */}
        <form onSubmit={buscarComandaPorNumero} style={{ display: 'flex', gap: 6, marginBottom: 10 }}>
          <input
            type="number"
            min="1" max="200"
            placeholder="🎫 Buscar comanda (nº)..."
            value={buscaComandaNum}
            onChange={e => { setBuscaComandaNum(e.target.value); setResultadoComanda(null); }}
            style={{ flex: 1, padding: '7px 10px', border: '1px solid #ddd', borderRadius: 8, fontSize: 13 }}
          />
          <button type="submit" disabled={buscandoComanda || !buscaComandaNum}
            style={{ padding: '7px 12px', background: '#1a1a2e', color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>
            {buscandoComanda ? '...' : '🔍'}
          </button>
        </form>
        {resultadoComanda === false && (
          <p style={{ fontSize: 12, color: '#e53935', marginBottom: 8, marginTop: -6 }}>
            Cartão não encontrado ou já encerrado.
          </p>
        )}
        {resultadoComanda && resultadoComanda !== false && (
          <div style={{ background: '#e8f5e9', border: '2px solid #4caf50', borderRadius: 8, padding: '8px 12px', marginBottom: 10, display: 'flex', alignItems: 'center', gap: 8 }}>
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 700, fontSize: 13 }}>🎫 Cartão #{resultadoComanda.numero}</div>
              <div style={{ fontSize: 12, color: '#555' }}>
                🪑 Mesa {resultadoComanda.mesa_numero}
                {resultadoComanda.cliente_nome ? ` · 👤 ${resultadoComanda.cliente_nome}` : ''}
              </div>
              <div style={{ fontSize: 12, color: '#2e7d32', fontWeight: 600 }}>{formatCurrency(resultadoComanda.total)}</div>
            </div>
            <button
              onClick={() => irParaMesaDaComanda(resultadoComanda)}
              style={{ background: '#2e7d32', color: '#fff', border: 'none', borderRadius: 8, padding: '6px 12px', cursor: 'pointer', fontSize: 12, fontWeight: 700 }}>
              Ir para mesa →
            </button>
          </div>
        )}

        <div className="pdv-tipo-row">
          <button className={`tipo-btn ${tipo === 'balcao' ? 'active' : ''}`} onClick={() => { setTipo('balcao'); setMesaId(''); }}>🏪 Balcão</button>
          <button className={`tipo-btn ${tipo === 'mesa' ? 'active' : ''}`} onClick={() => setTipo('mesa')}>🪑 Mesa</button>
        </div>

        {tipo === 'mesa' && (
          <select className="mesa-select" value={mesaId} onChange={e => setMesaId(e.target.value)}>
            <option value="">Selecionar mesa...</option>
            {mesas.map(m => (
              <option key={m.id} value={m.id}>
                Mesa {m.numero} — {m.status}{m.total_consumo > 0 ? ` (${formatCurrency(m.total_consumo)})` : ''}
              </option>
            ))}
          </select>
        )}

        <div className="pdv-itens">
          {/* Consumo existente da mesa agrupado por comanda */}
          {consumoMesa.loading && (
            <p className="pdv-vazio">Carregando consumo da mesa...</p>
          )}
          {!consumoMesa.loading && temConsumoMesa && (
            <div className="pdv-consumo-mesa">
              <div className="pdv-consumo-header">🍽 Consumo da mesa</div>

              {/* Por comanda */}
              {consumoMesa.comandas.map(c => (
                <div key={c.id} className="pdv-consumo-comanda">
                  <div className="pdv-consumo-comanda-titulo">
                    <span>📋 {c.cliente_nome || `Comanda #${c.numero}`}</span>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span className="pdv-consumo-comanda-total">{formatCurrency(c.total)}</span>
                      <button
                        className="btn"
                        style={{ padding: '2px 8px', fontSize: 12, background: '#e3f2fd', color: '#1565c0', border: '1px solid #90caf9' }}
                        onClick={() => imprimirComandaPDV(c)}
                        title="Imprimir comanda"
                      >
                        🖨 Imprimir
                      </button>
                      {parseFloat(c.total) === 0 && (
                        <button
                          className="btn"
                          style={{ padding: '2px 8px', fontSize: 12, background: '#fce4ec', color: '#c62828', border: '1px solid #ef9a9a' }}
                          onClick={() => cancelarComandaVazia(c)}
                          title="Cancelar comanda vazia"
                        >
                          ✕ Cancelar
                        </button>
                      )}
                      {c.total > 0 && (
                        <button
                          className="btn btn-success"
                          style={{ padding: '2px 8px', fontSize: 12 }}
                          onClick={() => abrirPagamentoComanda(c)}
                        >
                          💳 Pagar
                        </button>
                      )}
                    </div>
                  </div>
                  {c.itens.map((item, idx) => {
                    const st = STATUS_PROD[item.producao_status] || STATUS_PROD.fila;
                    const podeCanc = podeCancelarItem && item.producao_status !== 'entregue';
                    return (
                      <div key={idx} className="pdv-item pdv-item-consumo">
                        <span className="pdv-item-emoji">{item.emoji || '🥟'}</span>
                        <span className="pdv-item-nome">{item.nome}</span>
                        <span style={{ fontSize: 10, fontWeight: 700, color: st.color, flexShrink: 0, minWidth: 58, textAlign: 'center' }}>
                          {st.label}
                        </span>
                        <span className="pdv-item-qty-static">x{item.quantidade}</span>
                        <span className="pdv-item-total">{formatCurrency(item.subtotal)}</span>
                        {podeCanc && (
                          <button
                            onClick={() => cancelarItemComanda(c, item)}
                            title="Cancelar item"
                            style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#c62828', fontSize: 14, padding: '0 2px', flexShrink: 0 }}
                          >✕</button>
                        )}
                      </div>
                    );
                  })}
                  {c.itens.length === 0 && (
                    <p style={{ fontSize: 12, color: '#aaa', padding: '2px 0 4px' }}>Sem itens ainda</p>
                  )}
                </div>
              ))}

              {/* Pedidos avulsos (sem comanda) */}
              {consumoMesa.sem_comanda.itens.length > 0 && (
                <div className="pdv-consumo-comanda">
                  <div className="pdv-consumo-comanda-titulo">
                    <span>🧾 Consumo direto</span>
                    <span className="pdv-consumo-comanda-total">{formatCurrency(consumoMesa.sem_comanda.total)}</span>
                  </div>
                  {consumoMesa.sem_comanda.itens.map((item, idx) => (
                    <div key={idx} className="pdv-item pdv-item-consumo">
                      <span className="pdv-item-emoji">{item.emoji || '🥟'}</span>
                      <span className="pdv-item-nome">{item.nome}</span>
                      <span className="pdv-item-qty-static">x{item.quantidade}</span>
                      <span className="pdv-item-total">{formatCurrency(item.subtotal)}</span>
                    </div>
                  ))}
                </div>
              )}

              <div className="pdv-consumo-subtotal">
                Total consumo: <strong>{formatCurrency(consumoMesa.total)}</strong>
              </div>
            </div>
          )}

          {/* Novos itens adicionados no PDV */}
          {carrinho.length === 0 && !temConsumoMesa && !consumoMesa.loading && (
            <p className="pdv-vazio">Adicione produtos ao pedido</p>
          )}
          {carrinho.length > 0 && temConsumoMesa && (
            <div className="pdv-consumo-header pdv-novos-header">+ Adicionar ao pedido</div>
          )}
          {carrinho.map(item => (
            <div key={item.produto_id} className="pdv-item">
              <span className="pdv-item-emoji">{item.emoji || '🥟'}</span>
              <span className="pdv-item-nome">{item.nome}</span>
              <div className="pdv-item-qty">
                <button onClick={() => removerDoCarrinho(item.produto_id)}>−</button>
                <span>{item.quantidade}</span>
                <button onClick={() => adicionarAoCarrinho({ id: item.produto_id, nome: item.nome, emoji: item.emoji, preco: item.preco })}>+</button>
              </div>
              <span className="pdv-item-total">{formatCurrency(item.preco * item.quantidade)}</span>
            </div>
          ))}
        </div>

        <div className="pdv-total">
          <span>Total</span>
          <strong>{formatCurrency(total)}</strong>
        </div>

        <button
          className="btn btn-success btn-block pdv-btn-pagar"
          disabled={!podeCobrar}
          onClick={() => {
            if (temConsumoMesa && mesaId) {
              displayService.enviarMesa(mesaId).catch(() => {});
            } else if (!temConsumoMesa && carrinho.length > 0) {
              const itensDisplay = carrinho.map(i => ({
                nome: i.nome,
                emoji: i.emoji,
                preco_unit: i.preco,
                quantidade: i.quantidade,
                subtotal: i.preco * i.quantidade,
              }));
              displayService.enviarAvulso(itensDisplay, totalCarrinho).catch(() => {});
            }
            setModalPagamento(true);
          }}
        >
          💳 Cobrar {formatCurrency(total)}
        </button>

        {(carrinho.length > 0 || temConsumoMesa) && (
          <button className="btn btn-outline btn-block" style={{marginTop: 8}} onClick={() => setCarrinho([])}>
            🗑 Limpar novos itens
          </button>
        )}
      </div>

      {/* Modal Pagamento */}
      {modalPagamento && (
        <div className="pdv-modal-overlay" onClick={() => !processando && setModalPagamento(false)}>
          <div className="pdv-modal" onClick={e => e.stopPropagation()}>
            <h2>💳 Pagamento</h2>
            {temConsumoMesa && (
              <p className="pdv-modal-mesa-info">
                Mesa {mesas.find(m => String(m.id) === String(mesaId))?.numero} — {consumoMesa.pedidos_ids.length} pedido(s)
              </p>
            )}
            <p className="modal-total">{formatCurrency(total)}</p>

            <div className="forma-grid">
              {[
                { value: 'dinheiro', label: '💵 Dinheiro' },
                { value: 'cartao_debito', label: '💳 Débito' },
                { value: 'cartao_credito', label: '💳 Crédito' },
                { value: 'pix', label: '⚡ PIX' },
                { value: 'fiado', label: '📒 Fiado' },
              ].map(f => (
                <button
                  key={f.value}
                  className={`forma-btn ${forma === f.value ? 'active' : ''}`}
                  onClick={() => { setForma(f.value); if (f.value !== 'fiado') resetarCliente(); }}
                >
                  {f.label}
                </button>
              ))}
            </div>

            {(forma === 'cartao_debito' || forma === 'cartao_credito') && (
              <div className="form-group">
                <label>Operadora do cartão</label>
                <select value={bandeira} onChange={e => setBandeira(e.target.value)} autoFocus>
                  <option value="">Selecione a operadora...</option>
                  {operadoras.map(o => (
                    <option key={o.id} value={o.nome}>{o.nome}</option>
                  ))}
                </select>
              </div>
            )}

            {forma === 'dinheiro' && (
              <div className="form-group">
                <label>Valor recebido (R$)</label>
                <input
                  type="number"
                  step="0.01"
                  value={valorRecebido}
                  onChange={e => setValorRecebido(e.target.value)}
                  placeholder={total.toFixed(2)}
                  autoFocus
                />
                {valorRecebido && (
                  <p className="troco-info">Troco: <strong>{formatCurrency(troco)}</strong></p>
                )}
              </div>
            )}

            {forma === 'fiado' && (
              <div className="form-group" style={{ position: 'relative' }}>
                <label>Cliente *</label>
                {clienteSelecionado ? (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', background: '#e8f5e9', borderRadius: 'var(--radius)', border: '2px solid #4caf50' }}>
                    <span style={{ flex: 1, fontWeight: 600 }}>👤 {clienteSelecionado.nome}</span>
                    {clienteSelecionado.telefone && <span style={{ fontSize: 12, color: '#555' }}>{clienteSelecionado.telefone}</span>}
                    <button type="button" style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#c62828', fontSize: 16 }} onClick={resetarCliente}>✕</button>
                  </div>
                ) : (
                  <>
                    <input
                      type="text"
                      placeholder="Digite nome ou telefone do cliente..."
                      value={buscaCliente}
                      onChange={e => buscarClientes(e.target.value)}
                      autoFocus
                    />
                    {resultadosCliente.length > 0 && (
                      <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, background: '#fff', border: '2px solid var(--gray)', borderRadius: 'var(--radius)', zIndex: 10, maxHeight: 180, overflowY: 'auto' }}>
                        {resultadosCliente.map(c => (
                          <div
                            key={c.id}
                            onClick={() => selecionarCliente(c)}
                            style={{ padding: '8px 12px', cursor: 'pointer', borderBottom: '1px solid var(--gray)' }}
                            onMouseEnter={e => e.currentTarget.style.background = '#f5f5f5'}
                            onMouseLeave={e => e.currentTarget.style.background = '#fff'}
                          >
                            <strong>{c.nome}</strong>
                            {c.telefone && <span style={{ marginLeft: 8, fontSize: 12, color: '#777' }}>{c.telefone}</span>}
                          </div>
                        ))}
                      </div>
                    )}
                    {buscaCliente.length >= 2 && resultadosCliente.length === 0 && (
                      <p style={{ fontSize: 12, color: '#f57c00', marginTop: 4 }}>Nenhum cliente encontrado. Cadastre em Clientes antes de registrar o fiado.</p>
                    )}
                  </>
                )}
              </div>
            )}

            <div className="modal-acoes">
              <button className="btn btn-outline" onClick={() => { setModalPagamento(false); resetarCliente(); }} disabled={processando}>
                Cancelar
              </button>
              <button
                className="btn"
                style={{ background: '#e3f2fd', color: '#1565c0', border: '1px solid #90caf9' }}
                onClick={imprimirCobrancaPDV}
                disabled={processando}
              >
                🖨 Imprimir
              </button>
              <button
                className="btn btn-success"
                onClick={confirmarPedido}
                disabled={processando || (forma === 'fiado' && !clienteSelecionado) || (forma === 'dinheiro' && valorRecebido !== '' && parseFloat(valorRecebido) < total)}
              >
                {processando ? 'Processando...' : '✅ Confirmar'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Pagamento por Comanda */}
      {comandaParaPagar && (
        <div className="pdv-modal-overlay" onClick={() => !processandoComanda && setComandaParaPagar(null)}>
          <div className="pdv-modal" onClick={e => e.stopPropagation()}>
            <h2>💳 Pagar Comanda</h2>
            <p className="pdv-modal-mesa-info">
              {comandaParaPagar.cliente_nome || `Comanda #${comandaParaPagar.numero}`}
            </p>
            <p className="modal-total">{formatCurrency(comandaParaPagar.total)}</p>

            <div className="forma-grid">
              {[
                { value: 'dinheiro', label: '💵 Dinheiro' },
                { value: 'cartao_debito', label: '💳 Débito' },
                { value: 'cartao_credito', label: '💳 Crédito' },
                { value: 'pix', label: '⚡ PIX' },
                { value: 'fiado', label: '📒 Fiado' },
              ].map(f => (
                <button
                  key={f.value}
                  className={`forma-btn ${formaComanda === f.value ? 'active' : ''}`}
                  onClick={() => { setFormaComanda(f.value); if (f.value !== 'fiado') resetarCliente(); }}
                >
                  {f.label}
                </button>
              ))}
            </div>

            {(formaComanda === 'cartao_debito' || formaComanda === 'cartao_credito') && (
              <div className="form-group">
                <label>Operadora do cartão</label>
                <select value={bandeiraComanda} onChange={e => setBandeiraComanda(e.target.value)} autoFocus>
                  <option value="">Selecione a operadora...</option>
                  {operadoras.map(o => (
                    <option key={o.id} value={o.nome}>{o.nome}</option>
                  ))}
                </select>
              </div>
            )}

            {formaComanda === 'fiado' && (
              <div className="form-group" style={{ position: 'relative' }}>
                <label>Cliente *</label>
                {clienteSelecionado ? (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', background: '#e8f5e9', borderRadius: 'var(--radius)', border: '2px solid #4caf50' }}>
                    <span style={{ flex: 1, fontWeight: 600 }}>👤 {clienteSelecionado.nome}</span>
                    <button type="button" style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#c62828', fontSize: 16 }} onClick={resetarCliente}>✕</button>
                  </div>
                ) : (
                  <>
                    <input
                      type="text"
                      placeholder="Digite nome ou telefone..."
                      value={buscaCliente}
                      onChange={e => buscarClientes(e.target.value)}
                      autoFocus
                    />
                    {resultadosCliente.length > 0 && (
                      <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, background: '#fff', border: '2px solid var(--gray)', borderRadius: 'var(--radius)', zIndex: 10, maxHeight: 160, overflowY: 'auto' }}>
                        {resultadosCliente.map(c => (
                          <div key={c.id} onClick={() => selecionarCliente(c)}
                            style={{ padding: '8px 12px', cursor: 'pointer', borderBottom: '1px solid var(--gray)' }}
                            onMouseEnter={e => e.currentTarget.style.background = '#f5f5f5'}
                            onMouseLeave={e => e.currentTarget.style.background = '#fff'}
                          >
                            <strong>{c.nome}</strong>
                            {c.telefone && <span style={{ marginLeft: 8, fontSize: 12, color: '#777' }}>{c.telefone}</span>}
                          </div>
                        ))}
                      </div>
                    )}
                    {buscaCliente.length >= 2 && resultadosCliente.length === 0 && (
                      <p style={{ fontSize: 12, color: '#f57c00', marginTop: 4 }}>Nenhum cliente encontrado.</p>
                    )}
                  </>
                )}
              </div>
            )}

            {formaComanda === 'dinheiro' && (
              <div className="form-group">
                <label>Valor recebido (R$)</label>
                <input
                  type="number"
                  step="0.01"
                  value={valorRecebidoComanda}
                  onChange={e => setValorRecebidoComanda(e.target.value)}
                  placeholder={comandaParaPagar.total.toFixed(2)}
                  autoFocus
                />
                {valorRecebidoComanda && (
                  <p className="troco-info">
                    Troco: <strong>{formatCurrency(Math.max(0, parseFloat(valorRecebidoComanda) - comandaParaPagar.total))}</strong>
                  </p>
                )}
              </div>
            )}

            <div className="modal-acoes">
              <button className="btn btn-outline" onClick={() => setComandaParaPagar(null)} disabled={processandoComanda}>
                Cancelar
              </button>
              <button
                className="btn btn-success"
                onClick={confirmarPagamentoComanda}
                disabled={processandoComanda || (formaComanda === 'fiado' && !clienteSelecionado) || (formaComanda === 'dinheiro' && valorRecebidoComanda !== '' && parseFloat(valorRecebidoComanda) < comandaParaPagar.total)}
              >
                {processandoComanda ? 'Processando...' : '✅ Confirmar'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Cancelamento de Item */}
      {itemParaCancelar && (() => {
        const { item } = itemParaCancelar;
        const isFabricado  = item.tipo_estoque === 'fabricado';
        const isRevenda    = item.tipo_estoque === 'revenda';
        const emFila       = item.producao_status === 'fila';
        const statusLabel  = { fila: 'Na Fila', preparando: 'Preparando', pronto: 'Pronto' }[item.producao_status] || 'Na Fila';
        const custoEstimado = parseFloat(item.custo || 0) * parseFloat(item.quantidade);
        // Sem desperdício: revenda (qualquer status) ou fabricado ainda na fila
        const semDesperdicio = isRevenda || (isFabricado && emFila);
        return (
          <div className="pdv-modal-overlay" onClick={() => !cancelandoItem && setItemParaCancelar(null)}>
            <div className="pdv-modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 460 }}>
              <div style={{ textAlign: 'center', fontSize: 48, marginBottom: 8 }}>
                {semDesperdicio ? '📦' : '⚠️'}
              </div>
              <h2 style={{ textAlign: 'center', marginBottom: 4 }}>Cancelar Item</h2>
              <p style={{ textAlign: 'center', color: '#666', marginBottom: 16, fontSize: 14 }}>
                {item.emoji || '🥟'} <strong>{item.nome}</strong> — x{item.quantidade} — {formatCurrency(item.subtotal)}
              </p>

              {/* Status na cozinha */}
              <div style={{
                background: emFila ? '#f5f5f5' : '#fff3e0',
                border: `2px solid ${emFila ? '#ddd' : '#f57c00'}`,
                borderRadius: 10, padding: '10px 14px', marginBottom: 12,
              }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: emFila ? '#555' : '#e65100' }}>
                  🍳 Status na cozinha: <strong>{statusLabel}</strong>
                </div>
                {!emFila && (
                  <div style={{ fontSize: 12, color: '#e65100', marginTop: 4 }}>
                    Este item já entrou em produção.
                  </div>
                )}
              </div>

              {/* Consequência */}
              {isRevenda && (
                <div style={{ background: '#e8f5e9', border: '2px solid #4caf50', borderRadius: 10, padding: '10px 14px', marginBottom: 12 }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: '#2e7d32' }}>📦 Produto de Revenda</div>
                  <div style={{ fontSize: 12, color: '#388e3c', marginTop: 4 }}>
                    O insumo vinculado será devolvido ao estoque automaticamente. Sem perda.
                  </div>
                </div>
              )}
              {isFabricado && emFila && (
                <div style={{ background: '#e8f5e9', border: '2px solid #4caf50', borderRadius: 10, padding: '10px 14px', marginBottom: 12 }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: '#2e7d32' }}>🏭 Produto Fabricado — Ainda na Fila</div>
                  <div style={{ fontSize: 12, color: '#388e3c', marginTop: 4 }}>
                    Nenhum preparo foi iniciado. Os insumos da ficha técnica serão devolvidos ao estoque. Sem perda.
                  </div>
                </div>
              )}
              {isFabricado && !emFila && (
                <div style={{ background: '#fce4ec', border: '2px solid #e91e63', borderRadius: 10, padding: '10px 14px', marginBottom: 12 }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: '#880e4f' }}>🏭 Produto Fabricado — Perda Operacional</div>
                  <div style={{ fontSize: 12, color: '#ad1457', marginTop: 4 }}>
                    O preparo já foi iniciado. Os insumos não podem ser recuperados. Esta ação será registrada como <strong>perda operacional</strong>.
                    {custoEstimado > 0 && <span> Custo estimado: <strong>{formatCurrency(custoEstimado)}</strong>.</span>}
                  </div>
                </div>
              )}
              {!isRevenda && !isFabricado && (
                <div style={{ background: '#f5f5f5', border: '2px solid #ddd', borderRadius: 10, padding: '10px 14px', marginBottom: 12 }}>
                  <div style={{ fontSize: 13, color: '#555' }}>ℹ️ Produto sem vínculo de estoque. Nenhum impacto de estoque será gerado.</div>
                </div>
              )}

              <div className="modal-acoes">
                <button className="btn btn-outline" onClick={() => setItemParaCancelar(null)} disabled={cancelandoItem}>
                  Voltar
                </button>
                <button
                  className="btn"
                  onClick={confirmarCancelamentoItem}
                  disabled={cancelandoItem}
                  style={{ background: '#c62828', color: '#fff', border: 'none', flex: 1 }}
                >
                  {cancelandoItem ? 'Cancelando...' : '✕ Confirmar Cancelamento'}
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Modal Sucesso */}
      {ultimaVenda && (
        <div className="pdv-modal-overlay" onClick={() => setUltimaVenda(null)}>
          <div className="pdv-modal pdv-modal-sucesso" onClick={e => e.stopPropagation()}>
            <div className="sucesso-icone">✅</div>
            <h2>{ultimaVenda.comanda ? 'Comanda Paga!' : 'Venda Realizada!'}</h2>
            {ultimaVenda.comanda && (
              <div className="senha-balcao">{ultimaVenda.comanda}</div>
            )}
            {ultimaVenda.mesa && (
              <div className="senha-balcao">Mesa {ultimaVenda.mesa}</div>
            )}
            {ultimaVenda.senha && (
              <div className="senha-balcao">
                Senha: <strong>{ultimaVenda.senha}</strong>
              </div>
            )}
            <p>Total: <strong>{formatCurrency(ultimaVenda.total)}</strong></p>
            {ultimaVenda.troco > 0 && (
              <p>Troco: <strong>{formatCurrency(ultimaVenda.troco)}</strong></p>
            )}
            {ultimaVenda.mesaLivre && (
              <p style={{ color: '#4caf50', fontWeight: 600 }}>Mesa liberada!</p>
            )}
            <button className="btn btn-primary btn-block" onClick={() => setUltimaVenda(null)}>
              Nova Venda
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
